/**
 * SignatureCapture Component
 *
 * A reusable, domain-agnostic signature capture component.
 * Supports both drawn and typed signature methods with no business logic coupling.
 *
 * Features:
 * - Multiple signature methods (drawn, typed)
 * - Built-in validation with customizable rules
 * - Clean, consistent UI with shadcn/ui components
 * - Emits only data URLs and metadata
 * - No domain-specific dependencies
 *
 * @example
 * ```tsx
 * <SignatureCapture
 *   methods={['drawn', 'typed']}
 *   onSignatureComplete={(dataUrl, metadata) => {
 *     saveSignature(dataUrl, metadata);
 *   }}
 *   validation={{
 *     minStrokes: 5,
 *     maxSizeKB: 500
 *   }}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Pen, Type, Eraser, RefreshCw, AlertCircle, Loader2, Check } from 'lucide-react';
import { useSignatureCanvas, type SignatureValidation } from '@/hooks/useSignatureCanvas';
import { useTypedSignature, SIGNATURE_FONTS } from '@/hooks/useTypedSignature';

export interface SignatureMetadata {
  method: 'drawn' | 'typed';
  timestamp: string;
  font?: string;
  strokeCount?: number;
  sizeKB: number;
  dimensions: {
    width: number;
    height: number;
  };
}

export interface SignatureCaptureProps {
  // Configuration
  methods?: ('drawn' | 'typed')[];
  defaultMethod?: 'drawn' | 'typed';
  validation?: SignatureValidation;

  // Canvas dimensions
  width?: number;
  height?: number;

  // Styling
  className?: string;
  /** Optional class for the font-select dropdown content (portaled), e.g. "lw-ds" to brand-theme it. */
  selectContentClassName?: string;
  showValidationStatus?: boolean;

  // Initial values
  initialName?: string;

  // Callbacks
  onSignatureComplete: (dataUrl: string, metadata: SignatureMetadata) => void;
  onClear?: () => void;
  onMethodChange?: (method: 'drawn' | 'typed') => void;
}

export const SignatureCapture: React.FC<SignatureCaptureProps> = ({
  methods = ['drawn', 'typed'],
  defaultMethod = 'drawn',
  validation,
  width = 800,
  height = 200,
  className,
  selectContentClassName,
  showValidationStatus = true,
  initialName = '',
  onSignatureComplete,
  onClear,
  onMethodChange
}) => {
  const [currentMethod, setCurrentMethod] = useState<'drawn' | 'typed'>(defaultMethod);
  const [signatureAccepted, setSignatureAccepted] = useState(false);

  // Initialize hooks
  const drawnSignature = useSignatureCanvas({
    width,
    height,
    validation,
    autoInit: currentMethod === 'drawn'
  });

  const typedSignature = useTypedSignature({
    initialName,
    canvasWidth: width,
    canvasHeight: height
  });

  // Initialize drawn canvas when switching to it
  useEffect(() => {
    if (currentMethod === 'drawn' && !drawnSignature.isReady) {
      drawnSignature.initialize();
    }
  }, [currentMethod, drawnSignature]);

  // Handle method change
  const handleMethodChange = (method: string) => {
    const newMethod = method as 'drawn' | 'typed';
    setCurrentMethod(newMethod);
    setSignatureAccepted(false);
    onMethodChange?.(newMethod);
  };

  // Handle signature acceptance
  const handleAcceptSignature = () => {
    let dataUrl: string | null = null;
    let metadata: SignatureMetadata;

    if (currentMethod === 'drawn') {
      dataUrl = drawnSignature.getDataUrl();
      if (!dataUrl) {
return;
}

      const sizeInBytes = (dataUrl.length * 3) / 4;
      metadata = {
        method: 'drawn',
        timestamp: new Date().toISOString(),
        strokeCount: drawnSignature.strokeCount,
        sizeKB: Math.round(sizeInBytes / 1024),
        dimensions: { width, height }
      };
    } else {
      dataUrl = typedSignature.getDataUrl();
      if (!dataUrl) {
return;
}

      const sizeInBytes = (dataUrl.length * 3) / 4;
      metadata = {
        method: 'typed',
        timestamp: new Date().toISOString(),
        font: typedSignature.selectedFont,
        sizeKB: Math.round(sizeInBytes / 1024),
        dimensions: { width, height }
      };
    }

    setSignatureAccepted(true);
    onSignatureComplete(dataUrl, metadata);
  };

  // Handle clear
  const handleClear = () => {
    if (currentMethod === 'drawn') {
      drawnSignature.clear();
    } else {
      typedSignature.clear();
    }
    setSignatureAccepted(false);
    onClear?.();
  };

  // Check if signature is valid
  const isSignatureValid = currentMethod === 'drawn'
    ? drawnSignature.isValid()
    : typedSignature.isValid();

  // Get validation errors
  const validationErrors = currentMethod === 'drawn'
    ? drawnSignature.getValidationErrors()
    : [];

  return (
    <div className={className}>
      {/* Method selector (if multiple methods) */}
      {methods.length > 1 && (
        <Tabs
          value={currentMethod}
          onValueChange={handleMethodChange}
          className="w-full"
        >
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${methods.length}, 1fr)` }}>
            {methods.includes('drawn') && (
              <TabsTrigger value="drawn">
                <Pen className="h-4 w-4 mr-2" />
                Draw Signature
              </TabsTrigger>
            )}
            {methods.includes('typed') && (
              <TabsTrigger value="typed">
                <Type className="h-4 w-4 mr-2" />
                Type Signature
              </TabsTrigger>
            )}
          </TabsList>

          {/* Drawn signature tab */}
          {methods.includes('drawn') && (
            <TabsContent value="drawn" className="mt-4 space-y-3">
              {/* Error alert */}
              {drawnSignature.error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between">
                    <span>{drawnSignature.error}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={drawnSignature.retry}
                      className="ml-2"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Canvas area */}
              <div className="relative">
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={drawnSignature.canvasRef}
                    onMouseDown={drawnSignature.startDrawing}
                    onMouseMove={drawnSignature.draw}
                    onMouseUp={drawnSignature.stopDrawing}
                    onMouseLeave={drawnSignature.stopDrawing}
                    onTouchStart={drawnSignature.startDrawing}
                    onTouchMove={drawnSignature.draw}
                    onTouchEnd={drawnSignature.stopDrawing}
                    className="w-full cursor-crosshair"
                    style={{
                      height: `${height}px`,
                      touchAction: 'none'
                    }}
                  />
                  {!drawnSignature.isReady && !drawnSignature.error && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                      <div className="text-center">
                        <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Initializing canvas...</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Status and controls */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {drawnSignature.strokeCount > 0
                        ? `${drawnSignature.strokeCount} strokes`
                        : 'Start drawing your signature'}
                    </span>
                    {showValidationStatus && drawnSignature.hasSignature && (
                      <Badge
                        variant={isSignatureValid ? 'default' : 'secondary'}
                        className={isSignatureValid ? 'bg-green-500' : ''}
                      >
                        {isSignatureValid ? 'Valid' : 'Incomplete'}
                      </Badge>
                    )}
                    {signatureAccepted && (
                      <Badge variant="default" className="bg-blue-500">
                        <Check className="h-3 w-3 mr-1" />
                        Accepted
                      </Badge>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={drawnSignature.clear}
                    disabled={!drawnSignature.isReady || drawnSignature.strokeCount === 0}
                  >
                    <Eraser className="h-4 w-4 mr-2" />
                    Clear
                  </Button>
                </div>

                {/* Validation errors */}
                {showValidationStatus && validationErrors.length > 0 && (
                  <div className="mt-2">
                    {validationErrors.map((error, index) => (
                      <p key={index} className="text-xs text-red-500">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Typed signature tab */}
          {methods.includes('typed') && (
            <TabsContent value="typed" className="mt-4 space-y-4">
              {/* Name input */}
              <div>
                <Label htmlFor="typed-name" className="text-sm font-medium mb-2 block">
                  Your Full Name
                </Label>
                <Input
                  id="typed-name"
                  type="text"
                  placeholder="Type your name as you want it to appear"
                  value={typedSignature.name}
                  onChange={(e) => typedSignature.setName(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Font selector */}
              <div>
                <Label htmlFor="font-select" className="text-sm font-medium mb-2 block">
                  Signature Style
                </Label>
                <Select value={typedSignature.selectedFont} onValueChange={typedSignature.setSelectedFont}>
                  <SelectTrigger id="font-select" className="w-full">
                    <SelectValue placeholder="Choose a signature font" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClassName}>
                    {SIGNATURE_FONTS.map((font) => (
                      <SelectItem key={font.name} value={font.name}>
                        <span style={{ fontFamily: `"${font.name}", cursive` }}>
                          {font.display}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Font error alert */}
              {typedSignature.fontError && (
                <Alert variant="default" className="border-yellow-500 bg-yellow-50">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    Font loading failed, using fallback font. The signature will still be valid.
                  </AlertDescription>
                </Alert>
              )}

              {/* Preview */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Preview</Label>
                <Card className="p-4 bg-gray-50 border-2 border-dashed border-gray-300 min-h-[200px] flex items-center justify-center relative">
                  <canvas
                    ref={typedSignature.canvasRef}
                    className="absolute inset-0 w-full h-full"
                    style={{
                      visibility: typedSignature.signatureDataUrl ? 'hidden' : 'visible',
                      opacity: 0
                    }}
                    aria-hidden="true"
                  />

                  {typedSignature.isGenerating && typedSignature.name.trim() ? (
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Generating signature...</p>
                    </div>
                  ) : typedSignature.signatureDataUrl ? (
                    <div className="w-full flex flex-col items-center">
                      <img
                        src={typedSignature.signatureDataUrl}
                        alt="Signature preview"
                        className="max-w-full h-auto"
                        style={{ maxHeight: '150px' }}
                      />
                      <div className="flex items-center gap-2 mt-2">
                        {typedSignature.name.trim() && (
                          <p className="text-xs text-muted-foreground">
                            Font: {typedSignature.selectedFont}
                          </p>
                        )}
                        {signatureAccepted && (
                          <Badge variant="default" className="bg-blue-500">
                            <Check className="h-3 w-3 mr-1" />
                            Accepted
                          </Badge>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <Type className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Type your name above to see your signature</p>
                    </div>
                  )}
                </Card>
              </div>

              {/* Clear button for typed signature */}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={typedSignature.clear}
                  disabled={!typedSignature.name}
                >
                  <Eraser className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* Single method (no tabs) */}
      {methods.length === 1 && (
        <div className="space-y-4">
          {/* Content for single method would go here */}
          {/* Similar to above but without tabs wrapper */}
        </div>
      )}

      {/* Accept signature button */}
      <div className="mt-4">
        <Button
          type="button"
          onClick={handleAcceptSignature}
          disabled={!isSignatureValid || signatureAccepted}
          className="w-full"
        >
          {signatureAccepted ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Signature Accepted
            </>
          ) : (
            <>
              <Pen className="h-4 w-4 mr-2" />
              Accept Signature
            </>
          )}
        </Button>
      </div>

      {/* Clear all button (shown after acceptance) */}
      {signatureAccepted && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClear}
            className="w-full"
          >
            <Eraser className="h-4 w-4 mr-2" />
            Start Over
          </Button>
        </div>
      )}
    </div>
  );
};